"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  TABLE,
  TD,
  TEXTAREA,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  parseFasta,
  wrapSequence,
} from "@/lib/tool-kit/biology-genetics/fasta";
import { cleanNucleotides } from "@/lib/tool-kit/biology-genetics/iupac";
import {
  convertStrand,
  isReversePalindrome,
  type OutputAlphabet,
  type StrandOperation,
} from "@/lib/tool-kit/biology-genetics/strand";
import { downloadText } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const REVERSE_COMPLEMENT_GENERATOR_STRINGS = {
  inputLabel: "DNA or RNA sequence — plain text or FASTA",
  inputHint:
    "Spaces, line breaks and position numbers are removed. IUPAC ambiguity codes (R, Y, N…) and gaps (-) are kept; several FASTA records are all converted.",
  inputPlaceholder: ">my_primer\nGAATTCATGGCC",
  exampleHeader: "EcoRI site with flanking bases",
  tryExample: "Try an example",
  operationLabel: "Operation",
  opReverseComplement: "Reverse complement",
  opComplement: "Complement",
  opReverse: "Reverse",
  alphabetLabel: "Output letters",
  alphabetMatch: "Match the input (T for DNA, U for RNA)",
  alphabetDna: "DNA — use T",
  alphabetRna: "RNA — use U",
  caseLabel: "Letter case",
  caseKeep: "Keep as typed",
  caseUpper: "UPPER CASE",
  caseLower: "lower case",
  widthLabel: "Line length of the output",
  widthNone: "One line per sequence",
  width60: "60 characters",
  width70: "70 characters",
  width80: "80 characters",
  headerSuffix: "Add the operation to each FASTA header",
  suffixReverseComplement: "reverse complement",
  suffixComplement: "complement",
  suffixReverse: "reverse",
  results: "Results",
  outputLabel: "Converted sequence",
  copyOutput: "Copy",
  copied: "Copied!",
  download: "Download as FASTA",
  downloadName: "converted-sequence.fasta",
  sequenceColumn: "Sequence",
  lengthColumn: "Length (nt)",
  palindromeColumn: "Reads the same as its reverse complement?",
  yes: "Yes",
  no: "No",
  unnamed: "Sequence {n}",
  statLength: "Length",
  statSequences: "Sequences",
  unitNt: "nt",
  pairingHeading: "Both strands",
  pairingTop: "Your sequence, read 5′ to 3′",
  pairingBottom: "Complement, read 3′ to 5′",
  pairingLabel: "The two strands of the sequence paired base by base",
  prompt: "Paste a DNA or RNA sequence to convert it.",
  errorNone: "No nucleotide letters were found in that text.",
  ignored: "Ignored characters that are not nucleotide codes: {list}.",
  mixedTU: "The sequence contains both T and U; both are converted.",
} as const;

type CaseChoice = "keep" | "upper" | "lower";

const PAIRING_LIMIT = 100;
const OPERATIONS: readonly StrandOperation[] = [
  "reverseComplement",
  "complement",
  "reverse",
];
const EXAMPLE_SEQUENCE = "GAATTCATGGCCTTAAGC";

function applyCase(sequence: string, choice: CaseChoice): string {
  if (choice === "upper") {
    return sequence.toUpperCase();
  }
  return choice === "lower" ? sequence.toLowerCase() : sequence;
}

export default function ReverseComplementGeneratorWidget() {
  const strings = useToolUi(REVERSE_COMPLEMENT_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState(
    `>${strings.exampleHeader}\n${EXAMPLE_SEQUENCE}`
  );
  const [operation, setOperation] =
    useState<StrandOperation>("reverseComplement");
  const [alphabet, setAlphabet] = useState<OutputAlphabet>("match");
  const [letterCase, setLetterCase] = useState<CaseChoice>("keep");
  const [width, setWidth] = useState("0");
  const [addSuffix, setAddSuffix] = useState(true);

  const suffixes: Record<StrandOperation, string> = {
    reverseComplement: strings.suffixReverseComplement,
    complement: strings.suffixComplement,
    reverse: strings.suffixReverse,
  };
  const records = parseFasta(text, { uppercase: false });
  const items = records
    .map((record, index) => {
      const cleaned = cleanNucleotides(record.sequence, {
        keepCase: true,
        keepGaps: true,
      });
      const converted = convertStrand(cleaned.sequence, operation, alphabet);
      return {
        record,
        cleaned,
        label: record.name || t(strings.unnamed, { n: fmt(index + 1) }),
        output: applyCase(converted.sequence, letterCase),
        palindrome: isReversePalindrome(cleaned.sequence),
      };
    })
    .filter((item) => item.cleaned.sequence !== "");
  const invalid = [...new Set(items.flatMap((item) => item.cleaned.invalid))];
  const mixed = items.some((item) => item.cleaned.hasT && item.cleaned.hasU);
  const lineWidth = Number(width);
  const outputText = items
    .map((item) => {
      const body = wrapSequence(item.output, lineWidth);
      if (item.record.header === "") {
        return body;
      }
      const header = addSuffix
        ? `${item.record.header} (${suffixes[operation]})`
        : item.record.header;
      return `>${header}\n${body}`;
    })
    .join("\n");

  const first = items[0];
  const showPairing =
    items.length === 1 && first.cleaned.sequence.length <= PAIRING_LIMIT;
  const operationLabels: Record<StrandOperation, string> = {
    reverseComplement: strings.opReverseComplement,
    complement: strings.opComplement,
    reverse: strings.opReverse,
  };

  let message: string | null = null;
  if (text.trim() === "") {
    message = strings.prompt;
  } else if (items.length === 0) {
    message = strings.errorNone;
  }
  const totalLength = items.reduce(
    (sum, item) => sum + item.cleaned.sequence.length,
    0
  );

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="rcg-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={7}
        value={text}
      />
      <button
        className={BUTTON_SECONDARY}
        onClick={() =>
          setText(`>${strings.exampleHeader}\n${EXAMPLE_SEQUENCE}`)
        }
        type="button"
      >
        {strings.tryExample}
      </button>
      <PillGroup
        label={strings.operationLabel}
        name="rcg-operation"
        onChange={setOperation}
        options={OPERATIONS.map((value) => ({
          value,
          label: operationLabels[value],
        }))}
        value={operation}
      />
      <div className={`${CARD} space-y-4`}>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="rcg-alphabet"
            label={strings.alphabetLabel}
            onChange={setAlphabet}
            options={[
              { value: "match", label: strings.alphabetMatch },
              { value: "dna", label: strings.alphabetDna },
              { value: "rna", label: strings.alphabetRna },
            ]}
            value={alphabet}
          />
          <SelectField
            id="rcg-case"
            label={strings.caseLabel}
            onChange={setLetterCase}
            options={[
              { value: "keep", label: strings.caseKeep },
              { value: "upper", label: strings.caseUpper },
              { value: "lower", label: strings.caseLower },
            ]}
            value={letterCase}
          />
          <SelectField
            id="rcg-width"
            label={strings.widthLabel}
            onChange={setWidth}
            options={[
              { value: "0", label: strings.widthNone },
              { value: "60", label: strings.width60 },
              { value: "70", label: strings.width70 },
              { value: "80", label: strings.width80 },
            ]}
            value={width}
          />
        </div>
        <Checkbox
          checked={addSuffix}
          label={strings.headerSuffix}
          onChange={setAddSuffix}
        />
      </div>
      {invalid.length > 0 ? (
        <Notice>{t(strings.ignored, { list: invalid.join(" ") })}</Notice>
      ) : null}
      {mixed ? <Notice>{strings.mixedTU}</Notice> : null}
      {message ? <Notice>{message}</Notice> : null}
      {items.length > 0 ? (
        <div aria-live="polite" className="space-y-5">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.statLength}
              value={`${fmt(totalLength)} ${strings.unitNt}`}
            />
            <StatCard label={strings.statSequences} value={fmt(items.length)} />
          </StatGrid>
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="rcg-output"
            >
              {strings.outputLabel}
            </label>
            <textarea
              className={TEXTAREA}
              id="rcg-output"
              readOnly
              rows={7}
              value={outputText}
            />
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyOutput}
                text={outputText}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(strings.downloadName, `${outputText}\n`)
                }
                type="button"
              >
                {strings.download}
              </button>
            </div>
          </div>
          {showPairing ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.pairingHeading}</h3>
              <figure>
                <figcaption className="sr-only">
                  {strings.pairingLabel}
                </figcaption>
                <pre className="overflow-x-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm">
                  {`5′ ${first.cleaned.sequence} 3′\n   ${"|".repeat(first.cleaned.sequence.length)}\n3′ ${convertStrand(first.cleaned.sequence, "complement", alphabet).sequence} 5′`}
                </pre>
              </figure>
              <p className={HINT}>
                {strings.pairingTop} · {strings.pairingBottom}
              </p>
            </div>
          ) : null}
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH} scope="col">
                    {strings.sequenceColumn}
                  </th>
                  <th className={`${TH} text-right`} scope="col">
                    {strings.lengthColumn}
                  </th>
                  <th className={TH} scope="col">
                    {strings.palindromeColumn}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: records may share a name
                  <tr key={index}>
                    <td className={TD}>{item.label}</td>
                    <td className={`${TD} text-right`}>
                      {fmt(item.cleaned.sequence.length)}
                    </td>
                    <td className={TD}>
                      {item.palindrome ? strings.yes : strings.no}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
